/**
 * Trust Benefits & Footer Positioning
 *
 * Moves the trust benefits grid and footer contact elements
 * to their correct positions within the WooCommerce Blocks checkout.
 *
 * - Desktop: Benefits in sidebar after Total, Contact before policy links
 * - Mobile: Benefits as horizontal strip before Contact (both scrollable)
 *
 * @package SuperPymeCheckoutBlocks
 * @version 1.8.51
 */

(function() {
    'use strict';

    const DEBUG = window.location.search.includes('spcb_debug=1');
    const MOBILE_BREAKPOINT = 768;

    const log = (...args) => {
        if (DEBUG) {
            console.log('[SPCB Benefits]', ...args);
        }
    };

    /**
     * Check if we're on mobile
     */
    function isMobile() {
        return window.innerWidth < MOBILE_BREAKPOINT;
    }

    /**
     * Move trust benefits to correct position based on screen size
     * - Desktop: After Total row in sidebar
     * - Mobile: Before contact footer in main content
     */
    function moveTrustBenefits() {
        const benefits = document.getElementById('spcb-trust-benefits');
        if (!benefits) {
            log('Trust benefits element not found');
            return false;
        }

        // Already moved?
        if (benefits.dataset.spcbMoved === 'true') {
            return true;
        }

        // MOBILE: Place before contact footer in main content
        if (isMobile()) {
            // Try to place before contact footer
            const contactFooter = document.getElementById('spcb-footer-contact');
            if (contactFooter && contactFooter.parentNode) {
                contactFooter.parentNode.insertBefore(benefits, contactFooter);
                benefits.style.display = 'block';
                benefits.dataset.spcbMoved = 'true';
                benefits.classList.add('spcb-mobile-strip');
                log('Trust benefits moved before contact (mobile)');
                return true;
            }

            // Fallback: Place after checkout actions
            const actionsBlock = document.querySelector('.wc-block-checkout__actions');
            if (actionsBlock) {
                actionsBlock.parentNode.insertBefore(benefits, actionsBlock.nextSibling);
                benefits.style.display = 'block';
                benefits.dataset.spcbMoved = 'true';
                benefits.classList.add('spcb-mobile-strip');
                log('Trust benefits moved after actions (mobile fallback)');
                return true;
            }
        }

        // DESKTOP: Place after Total row in sidebar
        const totalFooterItem = document.querySelector('.wc-block-components-totals-footer-item');
        if (totalFooterItem) {
            const totalWrapper = totalFooterItem.closest('.wc-block-components-totals-wrapper');
            if (totalWrapper) {
                totalWrapper.parentNode.insertBefore(benefits, totalWrapper.nextSibling);
                benefits.style.display = 'block';
                benefits.dataset.spcbMoved = 'true';
                log('Trust benefits moved after Total row (desktop)');
                return true;
            }
        }

        // Fallback: Try sidebar order summary
        const sidebar = document.querySelector('.wc-block-checkout__sidebar');
        if (sidebar) {
            const orderSummary = sidebar.querySelector('.wp-block-woocommerce-checkout-order-summary-block');
            if (orderSummary) {
                orderSummary.appendChild(benefits);
                benefits.style.display = 'block';
                benefits.dataset.spcbMoved = 'true';
                log('Trust benefits appended to order summary (fallback)');
                return true;
            }
        }

        log('Could not find suitable position for trust benefits');
        return false;
    }

    /**
     * Move footer contact to after "Realizar pedido" but BEFORE policy links
     */
    function moveFooterContact() {
        const footer = document.getElementById('spcb-footer-contact');
        if (!footer) {
            log('Footer contact element not found');
            return false;
        }

        // Already moved?
        if (footer.dataset.spcbMoved === 'true') {
            return true;
        }

        // Priority 1: Insert BEFORE the policy links footer (if exists)
        const policyFooter = document.querySelector('.spcb-policy-footer');
        if (policyFooter) {
            policyFooter.parentNode.insertBefore(footer, policyFooter);
            footer.style.display = 'block';
            footer.dataset.spcbMoved = 'true';
            log('Footer contact moved before policy links');
            return true;
        }

        // Priority 2: Insert after checkout actions block
        const actionsBlock = document.querySelector('.wc-block-checkout__actions');
        if (actionsBlock) {
            actionsBlock.parentNode.insertBefore(footer, actionsBlock.nextSibling);
            footer.style.display = 'block';
            footer.dataset.spcbMoved = 'true';
            log('Footer contact moved after checkout actions');
            return true;
        }

        // Fallback: Find by WordPress block class
        const actionsBlockAlt = document.querySelector('.wp-block-woocommerce-checkout-actions-block');
        if (actionsBlockAlt) {
            actionsBlockAlt.parentNode.insertBefore(footer, actionsBlockAlt.nextSibling);
            footer.style.display = 'block';
            footer.dataset.spcbMoved = 'true';
            log('Footer contact moved after checkout actions (alt selector)');
            return true;
        }

        // Final fallback: Append to main content area
        const mainContent = document.querySelector('.wc-block-checkout__main');
        if (mainContent) {
            mainContent.appendChild(footer);
            footer.style.display = 'block';
            footer.dataset.spcbMoved = 'true';
            log('Footer contact appended to main content (fallback)');
            return true;
        }

        log('Could not find suitable position for footer');
        return false;
    }

    /**
     * Initialize element positioning
     */
    function init() {
        log('Initializing trust benefits positioning');

        // Initial attempt
        let benefitsMoved = moveTrustBenefits();
        let footerMoved = moveFooterContact();

        // If elements weren't moved, retry with MutationObserver
        if (!benefitsMoved || !footerMoved) {
            log('Setting up observer for dynamic elements');

            const observer = new MutationObserver((mutations, obs) => {
                if (!benefitsMoved) {
                    benefitsMoved = moveTrustBenefits();
                }

                if (!footerMoved) {
                    footerMoved = moveFooterContact();
                }

                // Stop observing once everything is moved
                if (benefitsMoved && footerMoved) {
                    log('All elements positioned, stopping observer');
                    obs.disconnect();
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Safety timeout - disconnect observer after 10 seconds
            setTimeout(() => {
                observer.disconnect();
                log('Observer timeout, disconnecting');
            }, 10000);
        }
    }

    /**
     * Update scroll indicators based on scroll position
     */
    function updateScrollIndicators(container) {
        if (!container) return;

        const scrollable = container.querySelector('.spcb-benefits-grid, .spcb-footer-contact-inner');
        if (!scrollable) return;

        const scrollLeft = scrollable.scrollLeft;
        const scrollWidth = scrollable.scrollWidth;
        const clientWidth = scrollable.clientWidth;
        const maxScroll = scrollWidth - clientWidth;

        // Remove all scroll state classes
        container.classList.remove('spcb-scroll-start', 'spcb-scroll-middle', 'spcb-scroll-end');

        if (maxScroll <= 0) {
            // No scroll needed, hide both indicators
            container.classList.add('spcb-scroll-end');
            return;
        }

        if (scrollLeft <= 5) {
            container.classList.add('spcb-scroll-start');
        } else if (scrollLeft >= maxScroll - 5) {
            container.classList.add('spcb-scroll-end');
        } else {
            container.classList.add('spcb-scroll-middle');
        }
    }

    /**
     * Initialize scroll indicators for mobile strips
     */
    function initScrollIndicators() {
        if (!isMobile()) return;

        const containers = document.querySelectorAll('.spcb-trust-benefits, .spcb-footer-contact');

        containers.forEach(container => {
            const scrollable = container.querySelector('.spcb-benefits-grid, .spcb-footer-contact-inner');
            if (!scrollable) return;

            // Initial state
            updateScrollIndicators(container);

            // Update on scroll
            scrollable.addEventListener('scroll', () => {
                updateScrollIndicators(container);
            }, { passive: true });
        });

        log('Scroll indicators initialized');
    }

    /**
     * Toggle expand/collapse on contact items
     */
    function initContactItemExpand() {
        document.addEventListener('click', (e) => {
            const item = e.target.closest('.spcb-footer-contact .spcb-footer-item');
            if (!item) return;

            // Don't expand if clicking a link (let the link work)
            if (e.target.tagName === 'A' || e.target.closest('a')) {
                return;
            }

            // Toggle expanded state
            item.classList.toggle('spcb-expanded');
            log('Contact item toggled:', item.classList.contains('spcb-expanded') ? 'expanded' : 'collapsed');
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            init();
            setTimeout(initScrollIndicators, 600);
            initContactItemExpand();
        });
    } else {
        // DOM already loaded, but WooCommerce Blocks might still be rendering
        // Wait a bit for React to render
        setTimeout(init, 500);
        setTimeout(initScrollIndicators, 1000);
        initContactItemExpand();
    }
})();
